import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Box, Typography, TextField, IconButton, Chip, Grid, Divider } from '@mui/material';
import { Close, Add, Remove } from '@mui/icons-material';
import { useHiddenExcelContext } from '../context/HiddenExcelContext';
import HiddenExcelCommand from '../../../services/command/hiddenExcel/HiddenExcelCommand.services';
import HiddenExcelQuery from '../../../services/query/hiddenExcel/HiddenExcelQuery.services';
import { SCUIButton } from '../../../atomic/atoms';
import Swal from 'sweetalert2';
import { HEX } from '../hiddenExcel.classes';

export const HiddenExcelDrawer = () => {
    const { t } = useTranslation();
    const { selectedTable, setTables, setIsLoading, setIsEditMode, setIsCreateMode, isEditMode, isCreateMode } =
        useHiddenExcelContext();

    const [formData, setFormData] = useState({
        name: '',
        fieldList: [] as string[],
        countryId: 0,
    });

    const [newField, setNewField] = useState('');

    const isDrawerOpen = isCreateMode || isEditMode;

    useEffect(() => {
        const selectedCountry = localStorage.getItem('selectedCountry');
        const countryId = selectedCountry ? JSON.parse(selectedCountry)?.countryId : 0;

        if (selectedTable && isEditMode) {
            setFormData({
                name: selectedTable.name || '',
                fieldList: selectedTable.fieldList || [],
                countryId: countryId,
            });
        } else if (isCreateMode) {
            setFormData({
                name: '',
                fieldList: [],
                countryId: countryId,
            });
        }
    }, [selectedTable, isEditMode, isCreateMode]);

    const handleInputChange = (field: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAddField = () => {
        if (newField.trim()) {
            setFormData((prev) => ({
                ...prev,
                fieldList: [...prev.fieldList, newField.trim()],
            }));
            setNewField('');
        }
    };

    const handleRemoveField = (index: number) => {
        setFormData((prev) => ({
            ...prev,
            fieldList: prev.fieldList.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            Swal.fire(t('general.error'), t('general.emptyFieldControlText'), 'error');
            return;
        }

        if (formData.fieldList.length === 0) {
            Swal.fire(t('general.error'), 'Please add at least one field', 'error');
            return;
        }

        try {
            setIsLoading(true);

            if (isEditMode && selectedTable) {
                await HiddenExcelCommand.UpdateHiddenExcelTable(selectedTable.id, formData);
                Swal.fire(t('hiddenExcel.tableUpdated'), '', 'success');
            } else {
                await HiddenExcelCommand.CreateHiddenExcelTable(formData).then((res) => {
                    if (res.name) {
                        Swal.fire(t('hiddenExcel.tableCreated'), '', 'success');
                    } else {
                        Swal.fire(t('general.error'), '', 'error');
                    }
                });
            }

            const selectedCountry = localStorage.getItem('selectedCountry');
            const countryId = selectedCountry ? JSON.parse(selectedCountry)?.countryId : undefined;

            const response = await HiddenExcelQuery.GetHiddenExcelTables({
                CountryId: countryId,
            });

            if (response) {
                setTables(response);
            }

            // Reset form
            setIsEditMode(false);
            setIsCreateMode(false);
        } catch (error) {
            console.error('Error saving table:', error);
            Swal.fire(t('general.error'), '', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setIsCreateMode(false);
    };

    return (
        <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={handleCancel}
            slotProps={{
                paper: {
                    className: HEX.drawerPaper,
                    sx: {
                        width: 400,
                        padding: 0,
                    },
                },
            }}
        >
            <Box className={HEX.drawerContent} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box className={HEX.drawerHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">
                        {isEditMode ? t('hiddenExcel.editTable') : t('hiddenExcel.addTable')}
                    </Typography>
                    <IconButton className={HEX.inputIconDrawerClose} onClick={handleCancel}>
                        <Close />
                    </IconButton>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box className={HEX.drawerScrollBody} sx={{ flex: 1, overflow: 'auto' }}>
                    <Grid container spacing={2} style={{ gap: '2rem' }}>
                        <Grid
                            style={{ marginTop: '2rem' }}
                            size={{
                                xs: 12
                            }}>
                            <TextField
                                className={HEX.inputTextTableName}
                                fullWidth
                                label={t('hiddenExcel.tableName')}
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                //required
                                disabled={isEditMode}
                            />
                        </Grid>

                        <Grid
                            className={HEX.drawerFieldsSection}
                            size={{
                                xs: 12
                            }}>
                            <Typography className={HEX.drawerFieldsSectionTitle} variant="subtitle1" sx={{ mb: 1 }}>
                                {t('hiddenExcel.fieldList')}
                            </Typography>

                            <Box className={HEX.drawerAddFieldRow} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <TextField
                                    className={HEX.inputTextFieldName}
                                    size="small"
                                    label={t('hiddenExcel.fieldName')}
                                    value={newField}
                                    onChange={(e) => setNewField(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddField()}
                                />
                                <SCUIButton
                                    className={HEX.inputButtonAddField}
                                    showStartIcon
                                    startIcon={<Add />}
                                    text={t('hiddenExcel.addField')}
                                    type="secondary"
                                    sizeType="fixed"
                                    size="m"
                                    onClick={handleAddField}
                                    disabled={!newField.trim()}
                                />
                            </Box>

                            <Box className={HEX.drawerFieldChipsWrap} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {formData.fieldList.map((field, index) => (
                                    <Chip
                                        key={`field-${field}-${index}`}
                                        className={HEX.drawerFieldChip}
                                        label={field}
                                        onDelete={() => handleRemoveField(index)}
                                        deleteIcon={<Remove />}
                                        sx={{
                                            backgroundColor: '#e3f2fd',
                                            color: '#1565c0',
                                            fontWeight: 500,
                                            border: '1px solid #90caf9',
                                            '& .MuiChip-deleteIcon': {
                                                color: '#1565c0',
                                                '&:hover': {
                                                    color: '#0d47a1',
                                                },
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box className={HEX.drawerFooter} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <SCUIButton
                        className={HEX.inputButtonDrawerCancel}
                        text={t('hiddenExcel.cancel')}
                        type="secondary"
                        sizeType="fixed"
                        size="m"
                        onClick={handleCancel}
                    />
                    <SCUIButton
                        className={HEX.inputButtonDrawerSubmit}
                        text={isEditMode ? t('hiddenExcel.save') : t('general.add')}
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        onClick={handleSubmit}
                        disabled={!formData.name.trim() || formData.fieldList.length === 0}
                    />
                </Box>
            </Box>
        </Drawer>
    );
};
